// settings.js
// The AI peer's per-group settings (currently just trigger mode) travel
// as a normal application-level message inside the group's own encrypted
// message stream — not a side channel, not local config on whichever
// device happens to run the AI peer. This was the explicit design
// decision: the setting should be visible/auditable to the whole group,
// not silently controlled by whoever's running the process.
//
// A settings message is just a group chat message whose text happens to
// be recognizable JSON with a type tag — group.js's sendMessage/
// receiveMessage don't need to know or care; this is purely an
// application-layer convention on top of them, the same way chat.js
// layers its own message types on top of the raw MLS byte stream.

import { isValidMode } from './trigger.js'

const TYPE_TAG = 'ai-peer-settings/v1'

/**
 * @param {string} triggerMode - one of TriggerMode
 * @param {string} setByFingerprint - identity fingerprint of whoever changed it
 * @returns {string} - ready to pass to group.sendMessage()
 */
export function formatSettingsMessage (triggerMode, setByFingerprint) {
  if (!isValidMode(triggerMode)) throw new Error(`Invalid trigger mode: ${triggerMode}`)
  return JSON.stringify({
    type: TYPE_TAG,
    triggerMode,
    setBy: setByFingerprint,
    at: new Date().toISOString()
  })
}

/**
 * @param {string} messageText - plaintext from group.receiveMessage()
 * @returns {{triggerMode: string, setBy: string, at: string} | null} -
 *   null if this message isn't a settings message (i.e. it's ordinary
 *   conversation — the overwhelming majority of messages).
 */
export function parseSettingsMessage (messageText) {
  let parsed
  try {
    parsed = JSON.parse(messageText)
  } catch {
    return null // not JSON at all — an ordinary chat message, not an error
  }
  if (parsed?.type !== TYPE_TAG) return null
  if (!isValidMode(parsed.triggerMode)) return null
  return { triggerMode: parsed.triggerMode, setBy: parsed.setBy, at: parsed.at }
}

/**
 * Tracks the current settings for however many groups the AI peer is in.
 * Fed by parseSettingsMessage() results as messages arrive; the AI peer's
 * message loop calls applyIfSettings() on every incoming message and
 * doesn't need to know the parsing details.
 */
export class GroupSettingsStore {
  constructor (defaultTriggerMode) {
    this.defaultTriggerMode = defaultTriggerMode
    this.byGroup = new Map() // groupId -> triggerMode
  }

  getTriggerMode (groupId) {
    return this.byGroup.get(groupId) ?? this.defaultTriggerMode
  }

  /** @returns {boolean} true if this message WAS a settings change (and was applied) */
  applyIfSettings (groupId, messageText) {
    const parsed = parseSettingsMessage(messageText)
    if (!parsed) return false
    this.byGroup.set(groupId, parsed.triggerMode)
    return true
  }
}
